/**
 * Data layer for `anonymizer-tor-block`. Thin typed fetch wrappers over /api/v1/anonymizer-tor-block.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export type AnonymizerTorBlockKind = "tor_exit_ip" | "anonymizer_umbrella";

export interface AnonymizerTorBlockRule {
	id: string;
	tenant_id: string;
	kind: AnonymizerTorBlockKind;
	value: string;
	note: string | null;
	created_at: number;
	created_by: string | null;
}

export interface ListAnonymizerTorBlockResponse {
	items: AnonymizerTorBlockRule[];
	next_cursor: number | null;
}

export interface CreateAnonymizerTorBlockBody {
	kind: AnonymizerTorBlockKind;
	value?: string;
	note?: string | null;
}

export interface CreateAnonymizerTorBlockResponse {
	ok: true;
	created: boolean;
	id: string;
	kind: AnonymizerTorBlockKind;
	value: string;
}

export interface EvaluateAnonymizerTorBlockBody {
	ip?: string;
	flags?: { tor?: boolean; anonymizer?: boolean };
}

export interface EvaluateAnonymizerTorBlockResponse {
	blocked: boolean;
	rule?: { id: string; kind: AnonymizerTorBlockKind; value: string };
}

export interface ListParams {
	kind?: AnonymizerTorBlockKind;
	limit?: number;
}

const BASE = "/api/v1/anonymizer-tor-block";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(
	params: ListParams = {},
): Promise<ListAnonymizerTorBlockResponse> {
	const qs = new URLSearchParams();
	if (params.kind) qs.set("kind", params.kind);
	if (params.limit != null) qs.set("limit", String(params.limit));
	const url = qs.toString() ? `${BASE}?${qs.toString()}` : BASE;
	const res = await fetch(url, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListAnonymizerTorBlockResponse>(res);
}

export async function create(
	body: CreateAnonymizerTorBlockBody,
): Promise<CreateAnonymizerTorBlockResponse> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<CreateAnonymizerTorBlockResponse>(res);
}

export async function remove(id: string): Promise<{ ok: true }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ ok: true }>(res);
}

export async function evaluate(
	body: EvaluateAnonymizerTorBlockBody,
): Promise<EvaluateAnonymizerTorBlockResponse> {
	const res = await fetch(`${BASE}/evaluate`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<EvaluateAnonymizerTorBlockResponse>(res);
}

export const anonymizerTorBlockQueryKeys = {
	all: ["anonymizer-tor-block"] as const,
	list: (params?: ListParams) =>
		params && (params.kind || params.limit != null)
			? ([...["anonymizer-tor-block"], "list", params] as const)
			: (["anonymizer-tor-block"] as const),
	detail: (id: string) =>
		["anonymizer-tor-block", id] as const,
};
